import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  BellRing,
  BellOff,
  CheckCheck,
  MessageSquare,
  CalendarClock,
  CalendarX,
  ClipboardList,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { notificationsAPI } from '../lib/api';
import { getPushState, enableWebPush, disableWebPush, type PushState } from '../lib/push';
import type { AppNotification } from '../types';

function iconFor(type: string) {
  switch (type) {
    case 'comment':
    case 'comment_reply':
      return MessageSquare;
    case 'plan_assigned':
    case 'plan_updated':
      return ClipboardList;
    case 'plan_expiring':
      return CalendarClock;
    case 'plan_expired':
      return CalendarX;
    default:
      return Bell;
  }
}

function timeAgo(iso: string): string {
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'recién';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `hace ${days} d`;
  return d.toLocaleDateString();
}

export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [push, setPush] = useState<PushState | null>(null);
  const [pushBusy, setPushBusy] = useState(false);

  const unreadQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsAPI.getUnreadCount(),
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const unread = unreadQuery.data?.count ?? 0;

  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationsAPI.getAll({ limit: 20 }),
    enabled: open,
  });

  useEffect(() => {
    if (open) getPushState().then(setPush).catch(() => {});
  }, [open]);

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsAPI.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: () => notificationsAPI.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const handleClick = (n: AppNotification) => {
    if (!n.isRead) markReadMutation.mutate(n.id);
    setOpen(false);
    if (n.data?.planId) navigate(`/plans/${n.data.planId}`);
    else if (n.data?.studentId) navigate(`/students/${n.data.studentId}`);
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push?.subscribed) {
        await disableWebPush();
        toast.success('Notificaciones push desactivadas');
      } else {
        await enableWebPush();
        toast.success('Notificaciones push activadas');
      }
      setPush(await getPushState());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo cambiar el push');
    } finally {
      setPushBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-xl text-dark-300 dark:text-dark-300 light:text-zinc-600 hover:bg-dark-800 dark:hover:bg-dark-800 light:hover:bg-zinc-100 transition-colors"
        title="Notificaciones"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-2 w-80 sm:w-96 z-50 rounded-xl bg-dark-800 dark:bg-dark-800 light:bg-white border border-dark-600 dark:border-dark-600 light:border-zinc-200 shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-dark-600 dark:border-dark-600 light:border-zinc-200">
                <span className="font-semibold text-dark-100 dark:text-dark-100 light:text-zinc-900">
                  Notificaciones
                </span>
                {unread > 0 && (
                  <button
                    onClick={() => markAllMutation.mutate()}
                    className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Marcar todo
                  </button>
                )}
              </div>

              {/* Push toggle */}
              {push?.supported && push?.configured && (
                <button
                  onClick={togglePush}
                  disabled={pushBusy}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm border-b border-dark-700/60 dark:border-dark-700/60 light:border-zinc-100 text-dark-300 dark:text-dark-300 light:text-zinc-600 hover:bg-dark-700/50 dark:hover:bg-dark-700/50 light:hover:bg-zinc-50 transition-colors disabled:opacity-50"
                >
                  {push.subscribed ? (
                    <>
                      <BellOff className="w-4 h-4" />
                      Desactivar push en este dispositivo
                    </>
                  ) : (
                    <>
                      <BellRing className="w-4 h-4 text-primary-400" />
                      Activar push en este dispositivo
                    </>
                  )}
                </button>
              )}

              {/* List */}
              <div className="max-h-96 overflow-y-auto">
                {listQuery.isLoading ? (
                  <div className="px-4 py-8 text-center text-sm text-dark-400">Cargando…</div>
                ) : (listQuery.data?.length ?? 0) === 0 ? (
                  <div className="px-4 py-10 text-center">
                    <Bell className="w-10 h-10 mx-auto text-dark-600 mb-2" />
                    <p className="text-sm text-dark-400">No tenés notificaciones</p>
                  </div>
                ) : (
                  listQuery.data!.map((n: AppNotification) => {
                    const Icon = iconFor(n.type);
                    return (
                      <button
                        key={n.id}
                        onClick={() => handleClick(n)}
                        className={`w-full text-left flex gap-3 px-4 py-3 border-b border-dark-700/40 dark:border-dark-700/40 light:border-zinc-100 hover:bg-dark-700/40 dark:hover:bg-dark-700/40 light:hover:bg-zinc-50 transition-colors ${
                          n.isRead ? 'opacity-60' : ''
                        }`}
                      >
                        <div className="w-9 h-9 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 text-primary-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-dark-100 dark:text-dark-100 light:text-zinc-900 truncate">
                            {n.title}
                          </p>
                          {n.body && (
                            <p className="text-xs text-dark-400 line-clamp-2">{n.body}</p>
                          )}
                          <p className="text-[11px] text-dark-500 mt-0.5">{timeAgo(n.createdAt)}</p>
                        </div>
                        {!n.isRead && (
                          <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-1.5" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
